import { Logger } from '@core/logger'

import type { EXPERIMENT_SLUGS } from '../constants/experimentConstants'
import type {
    TClosedExperimentsMap,
    TExperimentAssignment,
    TExperimentAssignmentFetcher,
    TExperimentCookieJar,
    TExperimentEventReporter,
    TExperimentSlug,
    TExperimentsInitConfig,
    TExperimentsStorage,
    TVariation,
    TVariationsMap,
} from '../types/experimentTypes'

const PARTICIPATED_KEY = 'active_experiments_slugs'
const SPLIT_KEY = 'audio_splitted_experiments'
const FAILURE_RETRY_MS = 5 * 60 * 1000

let activeExperiments: TVariationsMap = {}
let closedExperiments: TClosedExperimentsMap = {}
let forcedExperiments: TVariationsMap = {}
let storage: TExperimentsStorage | null = null
let reportEvent: TExperimentEventReporter = () => undefined
let cookies: TExperimentCookieJar | undefined
let ownSlugs: TExperimentSlug[] = []
let fetchAssignment: TExperimentAssignmentFetcher | undefined
let settled = new Set<TExperimentSlug>()
let failedAt = new Map<TExperimentSlug, number>()
let inFlight = new Map<TExperimentSlug, Promise<void>>()
let listeners = new Set<() => void>()

const ensureStorage = (): TExperimentsStorage => {
    if (!storage) {
        throw new Error(
            'experiments are not initialized — call experiments.init() in renderApp before reading flags',
        )
    }

    return storage
}

/**
 * The backend ships an empty map as `[]`: `ArrayableTrait::toArray()` ends on `ArrayHelper::toArray()`,
 * which unwraps every object it built, so `AbDto` cannot cast its way out. Normalise here, once,
 * rather than teaching every reader that the payload may be an array.
 */
const asMap = <T>(value: Record<string, T> | undefined): Record<string, T> =>
    value === undefined || Array.isArray(value) ? {} : value

const assignmentOf = (slug: EXPERIMENT_SLUGS): TExperimentAssignment | undefined =>
    forcedExperiments[slug] ?? activeExperiments[slug]

// nextjs writes its own slugs into the shared list and rebuilds it on an hourly sync this stack does
// not have. An empty answer is what a crawler and a failed resolution look like too, and audio-web
// prunes nothing on a failed fetch.
const pruneStaleSlugs = () => {
    const jar = cookies
    const store = storage

    if (
        jar === undefined ||
        store === null ||
        ownSlugs.length === 0 ||
        jar.getSubjectId() === undefined ||
        Object.keys(activeExperiments).length === 0
    ) {
        return
    }

    const participated = store.get(PARTICIPATED_KEY) ?? []
    const kept = participated.filter(
        slug =>
            !ownSlugs.includes(slug) ||
            activeExperiments[slug] !== undefined ||
            forcedExperiments[slug] !== undefined ||
            jar.readAssignment(slug) !== undefined,
    )

    if (kept.length !== participated.length) {
        store.set(PARTICIPATED_KEY, kept)
    }
}

const merge = (slug: TExperimentSlug, assignment: TExperimentAssignment) => {
    activeExperiments = { ...activeExperiments, [slug]: assignment }
}

const notify = () => {
    listeners.forEach(listener => listener())
}

const settle = (slug: TExperimentSlug) => {
    settled.add(slug)
    failedAt.delete(slug)
    notify()
}

const fail = (slug: TExperimentSlug) => {
    settled.add(slug)
    failedAt.set(slug, Date.now())
    notify()
}

const fetchAndSettle = (
    slug: TExperimentSlug,
    member: string,
    fetcher: TExperimentAssignmentFetcher,
) =>
    fetcher(slug, member).then(
        assignment => {
            inFlight.delete(slug)

            if (assignment) {
                merge(slug, assignment)
            }

            settle(slug)
        },
        (error: unknown) => {
            inFlight.delete(slug)
            Logger.error('Failed to fetch active experiment', { slug, error })
            fail(slug)
        },
    )

export const experimentCore = {
    init(config: TExperimentsInitConfig) {
        activeExperiments = asMap(config.activeExperiments)
        closedExperiments = asMap(config.closedExperiments)
        forcedExperiments = asMap(config.force)
        storage = config.storage
        reportEvent = config.reportEvent
        cookies = config.cookies
        ownSlugs = config.ownSlugs ?? []
        fetchAssignment = config.fetchAssignment
        settled = new Set()
        failedAt = new Map()
        inFlight = new Map()
        listeners = new Set()

        pruneStaleSlugs()
    },

    isSettled(slug: EXPERIMENT_SLUGS): boolean {
        if (assignmentOf(slug) !== undefined) {
            return true
        }

        if (!settled.has(slug)) {
            return false
        }

        const failed = failedAt.get(slug)

        return failed === undefined || Date.now() - failed < FAILURE_RETRY_MS
    },

    request(slug: EXPERIMENT_SLUGS): Promise<void> {
        if (experimentCore.isSettled(slug)) {
            return Promise.resolve()
        }

        const pending = inFlight.get(slug)

        if (pending) {
            return pending
        }

        const subject = cookies?.getSubjectId()

        if (subject === undefined) {
            settle(slug)

            return Promise.resolve()
        }

        const stored = cookies?.readAssignment(slug)

        if (stored !== undefined && (stored.member === undefined || stored.member === subject)) {
            merge(slug, { id: stored.id, variation: stored.variation })
            settle(slug)

            return Promise.resolve()
        }

        if (!fetchAssignment) {
            settle(slug)

            return Promise.resolve()
        }

        const call = fetchAndSettle(slug, subject, fetchAssignment)

        inFlight.set(slug, call)

        return call
    },

    subscribe(listener: () => void): () => void {
        listeners.add(listener)

        return () => {
            listeners.delete(listener)
        }
    },

    getVariation(slug: EXPERIMENT_SLUGS): TVariation | undefined {
        return assignmentOf(slug)?.variation
    },

    getInExperimentVariation(slug: EXPERIMENT_SLUGS, variation: TVariation = 2): boolean {
        return experimentCore.getVariation(slug) === variation
    },

    getAllExperimentsData(): { experimentIds: string[]; experimentVariations: string[] } {
        const merged: TVariationsMap = { ...activeExperiments, ...forcedExperiments }
        const experimentIds: string[] = []
        const experimentVariations: string[] = []

        Object.keys(merged).forEach(slug => {
            const assignment = merged[slug]

            if (assignment === undefined) {
                return
            }

            // Billing joins on the numeric id, so the slug stays on this side of the wire.
            experimentIds.push(String(assignment.id))
            experimentVariations.push(String(assignment.variation))
        })

        return { experimentIds, experimentVariations }
    },

    confirmParticipate(slug: EXPERIMENT_SLUGS) {
        if (Object.hasOwn(closedExperiments, slug)) {
            return
        }

        const store = ensureStorage()
        const participated = store.get(PARTICIPATED_KEY) ?? []
        const isTracked = participated.includes(slug)

        const track = () => {
            if (!isTracked) {
                store.set(PARTICIPATED_KEY, [...participated, slug])
            }
        }

        const assignment = assignmentOf(slug)

        if (assignment === undefined) {
            return
        }

        const stored = cookies?.readAssignment(slug)
        const subject = cookies?.getSubjectId()
        const recordsSameAssignment =
            stored !== undefined &&
            stored.id === assignment.id &&
            stored.variation === assignment.variation

        if (recordsSameAssignment && stored?.member === subject) {
            return
        }

        cookies?.writeAssignment(slug, assignment, subject)
        track()

        // The slug list outlives the assignment cookie by three weeks, so it answers "counted
        // already" whenever the cookie is gone or is only being stamped with a subject.
        const isAlreadyCounted =
            isTracked &&
            (stored === undefined || (recordsSameAssignment && stored.member === undefined))

        if (isAlreadyCounted) {
            return
        }

        reportEvent('Experiment User Participate', {
            experiment_id: assignment.id,
            variation: assignment.variation,
        })
    },

    confirmSplit() {
        const store = ensureStorage()
        const splitted = store.get(SPLIT_KEY) ?? []
        const next: TExperimentSlug[] = [...splitted]

        Object.keys(activeExperiments).forEach(slug => {
            if (next.includes(slug)) {
                return
            }

            const assignment = activeExperiments[slug]

            if (assignment === undefined) {
                return
            }

            next.push(slug)
            reportEvent('Experiment User Split', {
                experiment_id: assignment.id,
                variation: assignment.variation,
            })
        })

        store.set(SPLIT_KEY, next)
    },
}
